import type { Neighbourhood } from './types';
import type { Discovery } from './discovery';

export interface ProximityZones {
  watch: number;
  close: number;
  inner: number;
}
export const DEFAULT_ZONES: ProximityZones = { watch: 500, close: 100, inner: 50 };
export const PROXIMITY_COLOURS = ['#64869a', '#cf9c55', '#dc655b', '#ed6a61'] as const;
export const STEADY_RATE = 0.005;
export function validZones(z: ProximityZones): boolean {
  return (
    [z.inner, z.close, z.watch].every(Number.isFinite) &&
    z.inner >= 1 &&
    z.inner < z.close &&
    z.close < z.watch &&
    z.watch <= 50000
  );
}
export function proximityBand(rangeKm: number, zones = DEFAULT_ZONES): 0 | 1 | 2 | 3 {
  if (!Number.isFinite(rangeKm) || rangeKm < 0) return 0;
  return rangeKm <= zones.inner ? 3 : rangeKm <= zones.close ? 2 : rangeKm <= zones.watch ? 1 : 0;
}
export function motionDirection(rate: number): -1 | 0 | 1 {
  return !Number.isFinite(rate) || Math.abs(rate) < STEADY_RATE ? 0 : rate < 0 ? -1 : 1;
}
export function shouldPulse(range: number, rate: number, zones: ProximityZones, animate: boolean): boolean {
  return animate && proximityBand(range, zones) === 3 && motionDirection(rate) === -1;
}
export function arrowFraction(seconds: number, direction: -1 | 0 | 1, index: number): number {
  if (!direction) return 0.5;
  const phase = (seconds / 3) * direction + index * 0.5;
  return 0.15 + 0.7 * (phase - Math.floor(phase));
}

export interface BehaviourNotice {
  id: string;
  at: number;
  kind: 'Persistent proximity' | 'Repeated encounters' | 'Orbit solution changed';
  detail: string;
}
interface Track {
  inside: boolean;
  entries: number;
  lastEntry: number;
  slowSince: number | null;
  dwellReported: boolean;
}
/** Session-only model observations; gaps and time jumps cannot establish continuous dwell. */
export class BehaviourWatch {
  private last: number | null = null;
  private source = '';
  private tracks = new Map<string, Track>();
  notices: BehaviourNotice[] = [];
  reset() {
    this.last = null;
    this.source = '';
    this.tracks.clear();
    this.notices = [];
  }
  update(result: Neighbourhood, zones: ProximityZones, equivalent = new Set<string>()): BehaviourNotice[] {
    if (
      this.source !== result.selectedId ||
      (this.last !== null && (result.timestamp < this.last || result.timestamp - this.last > 10000))
    )
      this.reset();
    this.source = result.selectedId;
    if (this.last === result.timestamp) return this.notices;
    this.last = result.timestamp;
    const present = new Set<string>();
    for (const row of result.neighbours.slice(0, 512)) {
      if (equivalent.has(row.id) || !Number.isFinite(row.rangeKm)) continue;
      present.add(row.id);
      let track = this.tracks.get(row.id);
      if (!track) {
        track = { inside: false, entries: 0, lastEntry: -Infinity, slowSince: null, dwellReported: false };
        this.tracks.set(row.id, track);
      }
      const inside = row.rangeKm <= zones.close;
      if (inside && !track.inside && result.timestamp - track.lastEntry >= 60000) {
        track.entries++;
        track.lastEntry = result.timestamp;
        if (track.entries >= 2)
          this.add({
            id: row.id,
            at: result.timestamp,
            kind: 'Repeated encounters',
            detail: track.entries + ' entries into the close zone during this continuous session.',
          });
      }
      if (inside) track.inside = true;
      else if (row.rangeKm > zones.close * 1.03) track.inside = false;
      const slow = inside && row.relativeSpeedKmS !== undefined && row.relativeSpeedKmS <= 0.1;
      if (!slow) {
        track.slowSince = null;
        track.dwellReported = false;
      } else {
        track.slowSince ??= result.timestamp;
        if (result.timestamp - track.slowSince >= 300000 && !track.dwellReported) {
          track.dwellReported = true;
          this.add({
            id: row.id,
            at: result.timestamp,
            kind: 'Persistent proximity',
            detail: 'At least 5 minutes in the close zone with relative speed at or below 0.1 km/s.',
          });
        }
      }
    }
    for (const [id, track] of this.tracks)
      if (!present.has(id)) {
        track.slowSince = null;
        track.dwellReported = false;
      }
    // Bounded working state, not a durable event or audit archive.
    if (this.tracks.size > 512)
      for (const id of this.tracks.keys()) {
        if (!present.has(id)) this.tracks.delete(id);
        if (this.tracks.size <= 512) break;
      }
    return this.notices;
  }
  private add(notice: BehaviourNotice) {
    this.notices = [
      notice,
      ...this.notices.filter((n) => n.id !== notice.id || n.kind !== notice.kind),
    ].slice(0, 30);
  }
}

export function meaningfulApproachChange(previousKm: number, currentKm: number): boolean {
  return (
    [previousKm, currentKm].every(Number.isFinite) &&
    currentKm >= 0 &&
    previousKm - currentKm >= Math.max(5, previousKm * 0.2)
  );
}

export function forecastCurrent(
  forecast: Discovery | null,
  selectedId: string | null,
  revision: string,
  currentRevision: string | undefined,
  at: number,
): forecast is Discovery {
  return (
    !!forecast &&
    forecast.sourceId === selectedId &&
    revision === currentRevision &&
    Number.isFinite(at) &&
    at >= forecast.startMs &&
    at <= forecast.endMs
  );
}
